import { useQuery } from "@tanstack/react-query";

import { api } from "../api/rest";
import type {
  FamilyLink,
  FamilyRevRows,
  FamilyRoster,
  FamilySignal,
  FamilySummary,
} from "../api/types";

/**
 * Такт опроса консенсуса семьи кошельков.
 *
 * Тот же, что у соседних теневых сигналов: сводка и состав пула меняются
 * редко. Замер вне выборки дал 56 срабатываний в сутки — поток редкий, и
 * пятнадцать секунд опроса его не сглаживают, а просто не тратят лишнего.
 */
const FAMILY_REFETCH_MS = 15_000;

/**
 * Такт ЛЕНТЫ — чаще сводки, и по той же причине, что у почти решённого.
 *
 * Прежнее обоснование общего такта («кнопок у раздела нет, решений по нему не
 * принимают») с вехой живого режима перестало быть правдой: в ступени `alert`
 * строка ленты несёт кнопку «взять», и живёт та ровно до конца окна. При потоке
 * в 56 строк в сутки лишним запросом это не станет, а вот пропущенное окно
 * обошлось бы единственным за час предложением.
 */
const FAMILY_FEED_REFETCH_MS = 3_000;

/** Сводка консенсуса семьи: общий итог и разбивка по семьям одним ответом. */
export function useFamilySummary(hours: number, rev = 0, windowLen = 0) {
  return useQuery<{
    hours: number;
    rev: number;
    revisions: FamilyRevRows[];
    summary: FamilySummary;
    families: Record<string, FamilySummary>;
  }>({
    // Ревизия — часть КЛЮЧА запроса: срез меняет выборку, а не оформление, и
    // кеш прошлого среза выдал бы чужие числа под новым заголовком.
    queryKey: ["family-summary", hours, rev, windowLen],
    queryFn: () => api.familySummary(hours, rev, windowLen),
    refetchInterval: FAMILY_REFETCH_MS,
    retry: 1,
  });
}

/**
 * Лента сигналов консенсуса, свежие сверху.
 *
 * Ни счётчика «зачётные/погашенные», ни фильтра по умолчанию — журнал сам не
 * гасит строки правилом (в отличие от когорты): семья либо сошлась на окне,
 * либо строки не было.
 */
export function useFamilySignals(hours: number, limit = 60, rev = 0, windowLen = 0) {
  return useQuery<{ signals: FamilySignal[] }>({
    queryKey: ["family-signals", hours, limit, rev, windowLen],
    queryFn: () => api.familySignals(hours, limit, undefined, rev, windowLen),
    refetchInterval: FAMILY_FEED_REFETCH_MS,
    // В фоновой вкладке не опрашиваем: нажимать по строке некому.
    refetchIntervalInBackground: false,
    retry: 1,
  });
}

/**
 * Состав пула на одну ревизию вместе с уликами связи.
 *
 * Без `rev` — ПОСЛЕДНЯЯ ревизия: то, что оператор видит сейчас. Опрашивается
 * тем же тактом, что и сводка: состав меняется редко, но не самим ли этим
 * опросом узнаёт панель о новой ревизии.
 */
export function useFamilyPool(rev?: number) {
  return useQuery<{ roster: FamilyRoster; links: FamilyLink[] }>({
    queryKey: ["family-pool", rev],
    queryFn: () => api.familyPool(rev),
    refetchInterval: FAMILY_REFETCH_MS,
    retry: 1,
  });
}
